// @ts-nocheck
import { memo, useMemo, Fragment } from 'react'
import { number, string, bool, object, array, shape } from 'prop-types'
import styled from 'styled-components'
import { http } from '@services'
import { connect } from 'react-redux'
import { formatMiliseconds } from '@dmi-mch/utils'

import { mq, px2rem, formatGalleryLink, getSiteLanguage, hiDPIpx } from '@utils'
import { getGalleriesArray, getDimensions } from '@utils/catalog'
import { dimensions } from '@constants/catalog'
import { Text, CustomLinkTag } from '@components'
import { PriceLabel, Attachments } from '@dmi-mch/components'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'
import tokensRenamed from '@constants/tokensRenamed'
import { Container } from '@mch-group/uikit-components'

const ArtworkDescription = memo(({
  attachments,
  galleryName,
  galleryCleanTitle,
  galleryId,
  priceInUsd,
  priceInSecondCurrency,
  secondCurrency,
  fromPrice,
  toPrice,
  sold,
  medium,
  materials,
  unitMeasure,
  description,
  height,
  width,
  depth,
  edition,
  durationInMinute,
  weightInKilogram,
  volumeInCubicMetre,
  sponsorGalleries,
  labels,
  sharedLabels,
  framedDepth,
  framedHeight,
  framedUnitMeasure,
  framedWidth,
  reservedArtwork
}) => {
  const galleries = useMemo(() => (
    getGalleriesArray(galleryId, galleryCleanTitle, galleryName, sponsorGalleries)
  ), [galleryId, galleryCleanTitle, galleryName, sponsorGalleries])
  const frameMeasures = framedHeight && framedWidth && framedUnitMeasure

  return (
    <MainContainer className='px-5 px-lg-8 px-md-7'>
      <h2 className='ui header h5'>
        {galleries.map((item, index) => (
          <Fragment key={item.galleryId}>
            <CustomLinkTag
              url={formatGalleryLink(item.galleryId, item.galleryCleanTitle)}
              extraParams={{ id: item.galleryId, cleanTitle: item.galleryCleanTitle }}
            >
              <span>{item.galleryName}</span>
            </CustomLinkTag>
            {index < galleries.length - 1 ? ', ' : ''}
          </Fragment>
        ))}
      </h2>

      <PriceLabel
        {...{ priceInUsd, fromPrice, toPrice, sold, priceInSecondCurrency, secondCurrency }}
        soldLabel={labels.notAvailable}
      >
        {(price) => (
          <>
            <Text forwardedAs='div' isSoftText className='artwork-price'>
              {price} {!sold && reservedArtwork && (<span> | {labels.reservedArtworkLabel || 'Reserved'}</span>)}
            </Text>
            {/* not sold & secondaryCurrency is not ranged price so disclaimer is needed*/}
            {!sold && secondCurrency && (
              <Text forwardedAs='div' isSoftText className='price-disclaimer'>
                {labels.costDisclaimer}
              </Text>
            )}
            {/* not sold & not secondaryCurrency but exact in USD ,so disclaimer is needed*/}
            {!sold && !secondCurrency && !fromPrice && !toPrice && (
              <Text forwardedAs='div' isSoftText className='price-disclaimer'>
                {labels.costDisclaimer}
              </Text>
            )}
          </>
          )}
      </PriceLabel>

      <section>
        {medium && medium.name && <Text forwardedAs='div'>{medium.name}</Text>}
        {materials && <Text forwardedAs='div'>{materials}</Text>}
        {!!edition && <Text forwardedAs='div'>{edition}</Text>}
        {!!durationInMinute && <Text forwardedAs='div'>{formatMiliseconds(durationInMinute * 60 * 1000)}</Text>}
        {unitMeasure && [dimensions.cm, dimensions.inches].includes(unitMeasure.toUpperCase()) && height && width && (
          <>
            <ArtworkMeasures className={`${!frameMeasures ? 'last' : ''}`}>
              <Text forwardedAs='div'>{labels.artworkSizeLabel}</Text>
              <Text forwardedAs='div'>{getDimensions(height, width, depth, unitMeasure, dimensions.cm, labels.cm)}</Text>
              <Text forwardedAs='div'>{getDimensions(height, width, depth, unitMeasure, dimensions.inches, labels.inch)}</Text>
            </ArtworkMeasures>
            {frameMeasures && (
            <ArtworkMeasures className='last'>
              <Text forwardedAs='div'>{labels.artworkSizeWithFrameLabel}</Text>
              <Text forwardedAs='div'>{getDimensions(framedHeight, framedWidth, framedDepth, framedUnitMeasure.toLowerCase(),
                        dimensions.cm, labels.cm)}
              </Text>
              <Text forwardedAs='div'>{getDimensions(framedHeight, framedWidth, framedDepth, framedUnitMeasure.toLowerCase(),
                        dimensions.inches, labels.inch)}
              </Text>
            </ArtworkMeasures>
            )}
          </>
        )}
        {!!weightInKilogram && <Text forwardedAs='div'>{weightInKilogram} {labels.kg || 'kg'}</Text>}
        {!!volumeInCubicMetre && <Text forwardedAs='div'>{volumeInCubicMetre} {labels.m3 || 'm3'}</Text>}
      </section>

      {/* attachments */}
      {attachments && attachments.length > 0 && <Attachments
        isReadOnly
        visibleItemsOnStart={2}
        attachments={attachments}
        entityType={2} // 2, because it's artwork
        context={{ http }}
        className='attachments'
        preloadedLabels={sharedLabels}
        language={getSiteLanguage()}
      />}

      {description && (
        <Text forwardedAs='section' isHtml>
          {description}
        </Text>
      )}
    </MainContainer>
  )
})
ArtworkDescription.displayName = 'ArtworkDescription'

const MainContainer = styled(Container)`
  &&& {
    margin-bottom: ${px2rem(24)};

    .price-disclaimer {
      color: var(--bs-mch-gray-400);
      font-size: ${tokens.fontSize[1].value};
    }

    .h5 {
      font-size: ${tokens.fontSize[3].value};
      line-height: ${tokensRenamed.lineHeights[7].value};
      margin-bottom: ${px2rem(8)} !important;
    }

    section {
      margin-top: ${px2rem(30)};
      ${mq.lessThan('largestTablet')`
        margin-top: ${px2rem(24)};
      `}
    }

    .attachments {
      ${mq.between('tablet', 'largestTablet')`
        a {
          font-size: ${hiDPIpx(15)};
        }

        td {
          line-height: ${tokensRenamed.lineHeights[9].value};
        }
        .view-more {
          font-size: ${hiDPIpx(14)};
        }
      `}
    }
  }
`

const ArtworkMeasures = styled.div`
  margin-top: ${px2rem(23)};

  &.last {
    margin-bottom: ${px2rem(22)};
  }
`

ArtworkDescription.propTypes = {
  galleryId: number,
  galleryName: string,
  galleryCleanTitle: string,
  visiblePrice: bool,
  priceInUsd: number,
  priceInSecondCurrency: number,
  secondCurrency: string,
  fromPrice: number,
  toPrice: number,
  sold: bool,
  medium: object,
  materials: string,
  unitMeasure: string,
  description: string,
  height: number,
  width: number,
  depth: number,
  edition: string,
  durationInMinute: number,
  weightInKilogram: number,
  volumeInCubicMetre: number,
  sponsorGalleries: array,
  attachments: array,
  sharedLabels: object,
  framedDepth: number,
  framedHeight: number,
  framedUnitMeasure: string,
  framedWidth: number,
  labels: shape({
    min: string,
    cm: string,
    inch: string,
    kg: string,
    m3: string,
    notAvailable: string,
    reservedArtworkLabel: string
  }),
  reservedArtwork: bool
}

export default connect(
  ({ shared }) => ({
    sharedLabels: shared.labels
  }),
  null
)(ArtworkDescription)
